const MAX_SIZE = 400;
const QUALITY  = 0.82;

/**
 * Compress and cover-crop an image file to MAX_SIZE×MAX_SIZE JPEG.
 * @param {File} file
 * @returns {Promise<{ blob: Blob, dataUrl: string, qualityScore: number }>}
 */
export function compressImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      canvas.width  = MAX_SIZE;
      canvas.height = MAX_SIZE;
      const ctx = canvas.getContext('2d');

      // Cover-crop: scale so the shorter dimension fills MAX_SIZE, center-crop the longer
      const scale = Math.max(MAX_SIZE / img.width, MAX_SIZE / img.height);
      const sw = MAX_SIZE / scale;
      const sh = MAX_SIZE / scale;
      const sx = (img.width  - sw) / 2;
      const sy = (img.height - sh) / 2;

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, MAX_SIZE, MAX_SIZE);

      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
          const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
          const qualityScore = computeQualityScore(canvas, { sourceCropSize: sw });
          resolve({ blob, dataUrl, qualityScore });
        },
        'image/jpeg',
        QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Compress an already-loaded <img> element using a specific source crop rect.
 * @param {HTMLImageElement} imgElement  — the img element (must be loaded)
 * @param {{ sx, sy, sw, sh }} cropRect  — source rect in natural image pixels
 * @returns {Promise<{ blob: Blob, dataUrl: string, qualityScore: number }>}
 */
export function compressImageWithCrop(imgElement, { sx, sy, sw, sh }) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width  = MAX_SIZE;
    canvas.height = MAX_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgElement, sx, sy, sw, sh, 0, 0, MAX_SIZE, MAX_SIZE);
    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('Canvas toBlob failed')); return; }
        const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
        const qualityScore = computeQualityScore(canvas, { sourceCropSize: sw });
        resolve({ blob, dataUrl, qualityScore });
      },
      'image/jpeg',
      QUALITY
    );
  });
}

// ── Automated quality scoring ───────────────────────────────────────────────
//
// Fully rule-based, computed at compression time — no manual tagging needed.
// Combines three signals into a single 0–100 `qualityScore` that the
// auto-select-primary matching algorithm sorts/selects on:
//   - sharpness (Laplacian variance — the standard cheap blur detector)
//   - exposure (mean brightness + highlight/shadow clipping)
//   - effective resolution (whether the crop upscaled a too-small source region)

function computeSharpness(imageData) {
  const { data, width, height } = imageData;
  const gray = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    gray[i] = 0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2];
  }

  // Discrete Laplacian (4-neighbor) convolution, excluding the 1px border.
  // Variance of the response is the classic cheap blur-detection heuristic —
  // sharp edges produce large positive/negative spikes, blur flattens them.
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const lap = gray[i - width] + gray[i + width] + gray[i - 1] + gray[i + 1] - 4 * gray[i];
      sum += lap;
      sumSq += lap * lap;
      count++;
    }
  }
  const mean = sum / count;
  return sumSq / count - mean * mean;
}

function computeExposure(imageData) {
  const { data } = imageData;
  const numPixels = data.length / 4;
  let sum = 0;
  let clipped = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += lum;
    if (lum < 8 || lum > 247) clipped++;
  }
  const mean = sum / numPixels;
  const clippedFraction = clipped / numPixels;

  // Ideal mean brightness band: 70–190 out of 255
  const brightnessScore = mean < 70
    ? mean / 70
    : mean > 190
      ? Math.max(0, 1 - (mean - 190) / 65)
      : 1;

  const clippingScore = Math.max(0, 1 - clippedFraction * 4); // >25% blown/crushed pixels -> 0

  return brightnessScore * 0.6 + clippingScore * 0.4;
}

/**
 * @param {HTMLCanvasElement} canvas  — already drawn at MAX_SIZE×MAX_SIZE
 * @param {{ sourceCropSize: number }} opts  — source-pixel size of the crop
 *   region that was scaled onto the canvas (from compressImage/compressImageWithCrop's
 *   sw). Smaller than MAX_SIZE means the crop upscaled a too-small source region.
 * @returns {number} 0–100
 */
export function computeQualityScore(canvas, { sourceCropSize } = {}) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Empirically, an in-focus 400x400 JPEG thumbnail's Laplacian variance
  // lands well above this; treat it as the "fully sharp" ceiling.
  const sharpnessScore = Math.min(1, computeSharpness(imageData) / 400);
  const exposureScore  = computeExposure(imageData);
  const resolutionScore = sourceCropSize ? Math.min(1, sourceCropSize / MAX_SIZE) : 1;

  const combined = sharpnessScore * 0.5 + exposureScore * 0.3 + resolutionScore * 0.2;
  return Math.round(combined * 100);
}
